import { useEffect, useState, type RefObject } from 'react'

export interface ScrollFades {
  left: boolean
  right: boolean
}

// Which edges of a horizontal scroller currently hide content. Drives the
// edge fades so they appear only when there is something to scroll to, and
// only on the side it is hidden on.
export const useScrollFades = (ref: RefObject<HTMLElement | null>, deps: readonly unknown[] = []): ScrollFades => {
  const [fades, setFades] = useState<ScrollFades>({ left: false, right: false })

  useEffect(() => {
    const el = ref.current
    if (!el) {
      return
    }
    const update = () => {
      const max = el.scrollWidth - el.clientWidth
      const left = el.scrollLeft > 2
      const right = max - el.scrollLeft > 2
      setFades((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [ref, ...deps])

  return fades
}

const FADE = 32

/** CSS mask for the fades: none when nothing is hidden. */
export const scrollFadeMask = ({ left, right }: ScrollFades): string | undefined => {
  if (!left && !right) {
    return undefined
  }
  const start = left ? `transparent, #000 ${FADE}px` : '#000'
  const end = right ? `#000 calc(100% - ${FADE}px), transparent` : '#000'
  return `linear-gradient(to right, ${start}, ${end})`
}
